/**
 * API client for communicating with the backend.
 * Automatically attaches the current Azure access token before each request.
 * Includes error handling, response interceptors, and Sentry error capture.
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from "axios";
import * as Sentry from "@sentry/nextjs";
import { readStoredAuthSession } from "./auth/session";

// NUCLEAR OPTION: Get raw env var and log everything for debugging
const RAW_ENV_VAR = process.env.NEXT_PUBLIC_API_URL
console.log('🚨 NUCLEAR DEBUG - Raw env var:', RAW_ENV_VAR)
console.log('🚨 NUCLEAR DEBUG - Type:', typeof RAW_ENV_VAR)
console.log('🚨 NUCLEAR DEBUG - Length:', RAW_ENV_VAR?.length)

// Get API URL with MANDATORY HTTPS enforcement
let API_URL = RAW_ENV_VAR || 'http://localhost:8000'

console.log('🔍 Raw NEXT_PUBLIC_API_URL:', RAW_ENV_VAR)
console.log('🔍 Initial API_URL:', API_URL)
console.log('🔍 NODE_ENV:', process.env.NODE_ENV)
console.log('🔍 Window location:', typeof window !== 'undefined' ? window.location.href : 'server-side')

// CRITICAL: Force HTTPS for ALL cloudflare tunnel URLs
if (API_URL.includes('trycloudflare.com')) {
  if (API_URL.startsWith('http://')) {
    API_URL = API_URL.replace(/^http:\/\//, 'https://')
    console.log('🔒 Cloudflare tunnel FORCED to HTTPS:', API_URL)
  }
}

// MANDATORY: ALWAYS use HTTPS for ALL non-localhost URLs
// EXCEPT for private IPs (192.168.x.x, 10.x.x.x, 172.16-31.x.x) and public development IPs
// This prevents mixed content errors on Vercel and other HTTPS deployments
function isPrivateIP(url: string): boolean {
  const privateIPRegex = /^https?:\/\/(localhost|127\.0\.0\.1|192\.168\.\d+\.\d+|10\.\d+\.\d+\.\d+|172\.(1[6-9]|2[0-9]|3[01])\.\d+\.\d+|73\.176\.9\.108)/
  return privateIPRegex.test(url)
}

if (!API_URL.includes('localhost') && !API_URL.includes('127.0.0.1') && !isPrivateIP(API_URL)) {
  if (API_URL.startsWith('http://')) {
    API_URL = API_URL.replace(/^http:\/\//, 'https://')
    console.log('🔒 MANDATORY HTTPS UPGRADE:', API_URL)
  }
}

console.log('📡 Final API Base URL:', API_URL)
console.log('📡 Final API Base URL starts with https:', API_URL.startsWith('https://'))
console.log('📡 Final API Base URL full value:', JSON.stringify(API_URL))

// FINAL SAFETY CHECK before creating axios instance
// Allow HTTP for private IPs and localhost, but force HTTPS for public URLs
if (API_URL.startsWith('http://') && !isPrivateIP(API_URL)) {
  console.error('🚨🚨🚨 CRITICAL ERROR: API_URL is still HTTP after all checks!', API_URL)
  API_URL = API_URL.replace('http://', 'https://')
  console.error('🚨🚨🚨 EMERGENCY FIX: Forced to HTTPS:', API_URL)
}

console.log('🏗️ Creating axios instance with baseURL:', API_URL)

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

console.log('✅ Axios instance created with baseURL:', api.defaults.baseURL)

// Store the current Azure access token in memory for fast reuse between requests.
// The stored portal session remains the source of truth after page reloads.
let portalAccessToken: string | null = null

function resolvePortalAccessToken(): string | null {
  if (portalAccessToken && portalAccessToken.length > 0) {
    return portalAccessToken
  }

  const storedToken = readStoredAuthSession()?.accessToken ?? null
  if (storedToken && storedToken.length > 0) {
    portalAccessToken = storedToken
    return storedToken
  }

  return null
}

// Helper function to set the Azure access token used by portal API requests.
export const setBackendAuthToken = (token: string | null) => {
  portalAccessToken = token && token.length > 0 ? token : null
  if (portalAccessToken) {
    api.defaults.headers.common['Authorization'] = `Bearer ${portalAccessToken}`
    console.log('✅ Azure access token set')
  } else {
    delete api.defaults.headers.common['Authorization']
    console.log('❌ Azure access token cleared')
  }
}

// Request interceptor - automatically refresh token and fix mixed content issues
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    console.log('🔧 Interceptor - Request config BEFORE modifications:', {
      url: config.url,
      baseURL: config.baseURL,
      method: config.method
    })

    // NUCLEAR: Force HTTPS for ANY non-localhost URL (except private IPs)
    const forceHttps = (url: string | undefined) => {
      if (!url) return url
      if (url.includes('localhost') || url.includes('127.0.0.1')) return url
      if (isPrivateIP(url)) return url // Allow HTTP for private IPs
      if (url.startsWith('http://')) {
        console.log('🚨 INTERCEPTOR NUCLEAR: Forcing HTTP to HTTPS:', url)
        return url.replace('http://', 'https://')
      }
      return url
    }

    config.url = forceHttps(config.url)
    config.baseURL = forceHttps(config.baseURL)

    // CRITICAL: Fix mixed content by forcing HTTPS for cloudflare tunnels
    const needsHttpsUpgrade = (url: string | undefined) => {
      if (!url) return false
      return url.includes('trycloudflare.com') && url.startsWith('http://')
    }

    if (needsHttpsUpgrade(config.url)) {
      config.url = config.url!.replace(/^http:\/\//, 'https://')
      console.log('⚠️ Interceptor upgraded URL to HTTPS:', config.url)
    }

    if (needsHttpsUpgrade(config.baseURL)) {
      config.baseURL = config.baseURL!.replace(/^http:\/\//, 'https://')
      console.log('⚠️ Interceptor upgraded baseURL to HTTPS:', config.baseURL)
    }

    // Also upgrade if page is HTTPS (additional safety check)
    // BUT: Allow HTTP for private IPs and whitelisted public IPs
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
      if (config.url?.startsWith('http://') && !isPrivateIP(config.url)) {
        config.url = config.url.replace(/^http:\/\//, 'https://')
        console.log('⚠️ Interceptor upgraded URL (page is HTTPS):', config.url)
      }
      if (config.baseURL?.startsWith('http://') && !isPrivateIP(config.baseURL)) {
        config.baseURL = config.baseURL.replace(/^http:\/\//, 'https://')
        console.log('⚠️ Interceptor upgraded baseURL (page is HTTPS):', config.baseURL)
      }
    }

    console.log('🔧 Interceptor - Request config AFTER modifications:', {
      url: config.url,
      baseURL: config.baseURL,
      method: config.method
    })

    const token = resolvePortalAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
      console.log('✅ Azure access token added to Authorization header:', {
        tokenLength: token.length,
        url: config.url,
        tokenPreview: token.substring(0, 30) + '...'
      })
    } else {
      console.error('❌❌❌ CRITICAL: Azure access token is unavailable; refusing unauthenticated request')
      return Promise.reject(new AxiosError(
        'Azure access token is unavailable; refusing unauthenticated request',
        'ERR_BAD_REQUEST',
        config
      ))
    }
    console.log('🔧 Final Authorization header:', config.headers.Authorization ? 'SET' : 'NOT SET')
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling — captures to Sentry
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Capture all API errors to Sentry with full context
    Sentry.captureException(error, {
      tags: {
        component: 'api-client',
        error_type: error.response ? 'http' : 'network',
      },
      extra: {
        endpoint: error.config?.url || 'unknown',
        method: error.config?.method?.toUpperCase() || 'UNKNOWN',
        status: error.response?.status,
        statusText: error.response?.statusText,
        baseURL: error.config?.baseURL,
        responseData:
          typeof error.response?.data === 'object'
            ? JSON.stringify(error.response?.data).substring(0, 500)
            : String(error.response?.data || '').substring(0, 500),
      },
      fingerprint: ['axios-error', error.config?.url || 'unknown', String(error.response?.status || 'network')],
    })

    // Add breadcrumb for the failed request
    Sentry.addBreadcrumb({
      category: 'http',
      message: `${error.config?.method?.toUpperCase() || 'GET'} ${error.config?.url} → ${error.response?.status || 'NETWORK ERROR'}`,
      data: {
        status: error.response?.status,
        url: error.config?.url,
      },
      level: error.response?.status && error.response.status >= 500 ? 'error' : 'warning',
    })

    return Promise.reject(error)
  }
)

// Helper function to set the Azure access token (kept for backward compatibility)
// Note: The request interceptor handles token injection for every request
// This is a fallback for cases where the interceptor isn't used
export const setAuthToken = (token: string | null) => {
  setBackendAuthToken(token)
}

export default api
